import { Component, OnInit, ViewChild } from '@angular/core';
import { DominioService } from '../../../services/dominio.service';
import { AlertaModalService } from '../../../services/util/alerta-modal.service';
import { SpinnerService } from '../../../services/util/spinner.service';
import { FileUploadService } from '../../../services/file-upload.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { DominioDto } from '../../../dto/justino/dominio.dto';
import { ModalEditarVideoComponent } from '../modal-editar-video/modal-editar-video.component';

@Component({
  selector: 'app-gestion-videos',
  templateUrl: './gestion-videos.component.html',
  styleUrl: './gestion-videos.component.scss',
})
export class GestionVideosComponent implements OnInit {
  videos: DominioDto[] = [];
  videoForm = new FormGroup({
    videoName: new FormControl('', Validators.required),
    videoUrl: new FormControl('', Validators.required),
  });
  modalUpdate: boolean = false;
  selectedVideo: DominioDto | null = null;

  @ViewChild(ModalEditarVideoComponent)
  modalUpdateVideo!: ModalEditarVideoComponent;

  constructor(
    private readonly dominioService: DominioService,
    private readonly alertService: AlertaModalService,
    private readonly spinnerService: SpinnerService,
    private readonly fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    this.getDomainsWithVideoUrl();
  }

  getDomainsWithVideoUrl(): void {
    this.dominioService.getDominios().subscribe({
      next: response => {
        this.videos = response.filter(video => video.videoUrl !== null);
      },
      error: () => {
        this.alertService.error(
          'Error al obtener los dominios. Por favor, inténtelo de nuevo más tarde.'
        );
      },
    });
  }

  async processVideoUpload(): Promise<void> {
    try {
      const domain = await this.getProcessedVideo();
      if (!domain) {
        return;
      }
      this.spinnerService.hideSpinner();
      this.alertService.exito('Video procesado correctamente.');
      this.videoForm.reset();
      this.getDomainsWithVideoUrl();
    } catch (error) {
      this.spinnerService.hideSpinner();
      this.alertService.error(
        'Error al procesar el video. Por favor, inténtelo de nuevo más tarde.'
      );
    }
  }

  async getProcessedVideo(): Promise<DominioDto | null> {
    let createdDomain: DominioDto | null = null;
    try {
      if (this.videoForm.invalid) return null;

      const domain = {
        Id: '0',
        ParentId: '0',
        Nombre: this.videoForm.value.videoName!.replace(/\s+/g, '-'),
        Descripcion: 'Creado desde el administrador de videos',
        Estado: 'Activo',
        FechaCreacion: new Date().toISOString(),
        FechaModificacion: new Date().toISOString(),
        UsuarioCreacion: 'admin',
        UsuarioModificacion: 'admin',
        SubDominios: [],
        videoUrl: this.videoForm.value.videoUrl!,
      };

      createdDomain = await lastValueFrom(
        this.dominioService.createDominio(domain)
      );

      await lastValueFrom(
        this.dominioService.sendVideoProcessRequest(createdDomain, false)
      );

      return createdDomain;
    } catch (error) {
      if (createdDomain) {
        this.dominioService.deleteDominio(createdDomain.Id).subscribe({
          next: () => {},
          error: () => {
            this.alertService.error(
              'Error al subir el video.'
            );
          },
        });
      }
      if (error && (error as any).status == 400) {
        this.alertService.error((error as any).error.detail);
      }
      return null;
    }
  }

  async createVideoDomain(domain: DominioDto): Promise<DominioDto | null> {
    try {
      const createdDomain = await lastValueFrom(
        this.dominioService.createDominio(domain)
      );
      return createdDomain;
    } catch (err) {
      return null;
    }
  }

  createVideoDataset(base64Data: string, domainId: string): void {
    this.fileUploadService.uploadDomainFile(base64Data, domainId).subscribe({
      next: () => {
        this.spinnerService.hideSpinner();
        this.alertService.exito('El video se ha subido correctamente.');
        this.getDomainsWithVideoUrl();
        this.videoForm.reset();
      },
      error: () => {
        this.spinnerService.hideSpinner();
        this.dominioService.deleteDominio(domainId).subscribe(() => {
          this.alertService.error(
            'Error al subir el video. El dominio ha sido eliminado.'
          );
        });
      },
    });
  }

  openConfirmationDeleteVideoAlert(domainId: string): void {
    this.alertService.confirmacion(
      '¿Desea eliminar el video?',
      'Eliminar',
      () => {
        this.deleteVideoDomain(domainId);
      },
      () => {}
    );
  }

  deleteVideoDomain(domainId: string): void {
    this.dominioService.deleteDominio(domainId).subscribe({
      next: () => {
        this.spinnerService.hideSpinner();
        this.alertService.exito('Video eliminado correctamente.');
        this.getDomainsWithVideoUrl();
      },
      error: () => {
        this.spinnerService.hideSpinner();
        this.alertService.error(
          `Error al eliminar el video, por favor inténtelo de nuevo más tarde.`
        );
      },
    });
  }

  async updateVideoInfo(video: DominioDto) {
    this.selectedVideo = video;
    this.openUpdateModal();
  }

  openUpdateModal() {
    this.modalUpdate = true;
    setTimeout(() => {
      this.modalUpdateVideo.openCloseModal(this.selectedVideo);
    }, 500);
  }

  updateVideo(dto: any) {
    this.selectedVideo!.Nombre = dto.videoName;
    if (this.selectedVideo!.videoUrl !== dto.videoUrl) {      
      this.selectedVideo!.videoUrl = dto.videoUrl;
      this.dominioService.sendVideoProcessRequest(this.selectedVideo!, false).subscribe({
        next: () => {          
          this.dominioService.updateDominio(this.selectedVideo!.Id, this.selectedVideo!).subscribe({
            next: () => {
              this.alertService.exito('Video actualizado correctamente.');
              this.getDomainsWithVideoUrl();
            },
            error: err => {
              let errorMessage = err.statusText;
              if (err.error && err.error.detail) {
                errorMessage = err.error.detail;
              }
              this.alertService.error(
                `Error al actualizar el contenido del video: ${errorMessage}`
              );
            },
          });      
        },
        error: err => {
          let errorMessage = err.statusText;
          if (err.error && err.error.detail) {
            errorMessage = err.error.detail;
          }
          this.alertService.error(
            `Error al actualizar el contenido del video: ${errorMessage}`
          );
        },
      });
    } else {
      this.dominioService.updateDominio(this.selectedVideo!.Id, this.selectedVideo!).subscribe({
        next: () => {
          this.alertService.exito('Video actualizado correctamente.');
          this.getDomainsWithVideoUrl();
        },  
        error: err => {
          let errorMessage = err.statusText;
          if (err.error && err.error.detail) {
            errorMessage = err.error.detail;
          }
          this.alertService.error(
            `Error al actualizar el contenido del video: ${errorMessage}`
          );
        },
      });
    }
  }
}
